import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, FormControl, InputLabel, Select, MenuItem,
  Typography, IconButton, Box, Avatar, useTheme, alpha, Stack, TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Membre } from '../types';

interface Props {
  open: boolean;
  membres: Membre[];
  defaultSourceId?: number;
  onClose: () => void;
  onSave: (data: {
    membre_source: number; membre_cible: number; type_relation: string;
    date_debut?: string; date_fin?: string;
  }) => void;
}

// swap: true → inverser source/cible avant sauvegarde en DB
// dbType: le type réel stocké en base
const RELATIONS = [
  { value: 'parent',              label: 'est parent de',              dbType: 'parent',              swap: false },
  { value: 'enfant_de',           label: 'est enfant de',              dbType: 'parent',              swap: true  },
  { value: 'conjoint',            label: 'est conjoint(e) de',         dbType: 'conjoint',            swap: false },
  { value: 'ex_conjoint',         label: 'est ex-conjoint(e) de',      dbType: 'ex_conjoint',         swap: false },
  { value: 'grand_parent',        label: 'est grand-parent de',        dbType: 'grand_parent',        swap: false },
  { value: 'petit_enfant_de',     label: 'est petit-enfant de',        dbType: 'grand_parent',        swap: true  },
  { value: 'arriere_grand_parent',label: 'est arrière-grand-parent de',dbType: 'arriere_grand_parent',swap: false },
  { value: 'frere',               label: 'est frère de',               dbType: 'frere',               swap: false },
  { value: 'soeur',               label: 'est sœur de',                dbType: 'soeur',               swap: false },
  { value: 'demi_frere',          label: 'est demi-frère de',          dbType: 'demi_frere',          swap: false },
  { value: 'demi_soeur',          label: 'est demi-sœur de',           dbType: 'demi_soeur',          swap: false },
];

const genderColor = (sexe?: string) => {
  if (sexe === 'Homme') return '#3B82F6';
  if (sexe === 'Femme') return '#EC4899';
  return '#6B7280';
};

const MiniAvatar: React.FC<{ membre?: Membre; placeholder?: string }> = ({ membre, placeholder }) => {
  const theme = useTheme();
  if (!membre) {
    return (
      <Avatar
        sx={{
          width: 40,
          height: 40,
          bgcolor: alpha(theme.palette.text.primary, 0.06),
          color: 'text.disabled',
          fontSize: '1rem',
        }}
      >
        ?
      </Avatar>
    );
  }
  const color = genderColor(membre.sexe);
  return (
    <Avatar
      sx={{
        width: 40,
        height: 40,
        bgcolor: alpha(color, 0.15),
        color,
        fontWeight: 700,
        fontSize: '0.85rem',
      }}
    >
      {`${membre.prenom?.[0] || ''}${membre.nom?.[0] || ''}`.toUpperCase()}
    </Avatar>
  );
};

const RelationFormDialog: React.FC<Props> = ({
  open, membres, defaultSourceId, onClose, onSave,
}) => {
  const theme = useTheme();
  const [sourceId, setSourceId] = useState<number | ''>('');
  const [relationType, setRelationType] = useState('');
  const [targetId, setTargetId] = useState<number | ''>('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');

  useEffect(() => {
    if (open) {
      setSourceId(defaultSourceId ?? '');
      setRelationType('');
      setTargetId('');
      setDateDebut('');
      setDateFin('');
    }
  }, [open, defaultSourceId]);

  // Tri Nom puis Prénom pour les listes déroulantes
  const membresTries = [...membres].sort(
    (a, b) => a.nom.localeCompare(b.nom, 'fr') || a.prenom.localeCompare(b.prenom, 'fr')
  );

  const source = membres.find(m => m.id === sourceId);
  const target = membres.find(m => m.id === targetId);
  const canSubmit = sourceId !== '' && relationType !== '' && targetId !== '' && sourceId !== targetId;

  const isConjugal = relationType === 'conjoint' || relationType === 'ex_conjoint';
  const isDivorce = relationType === 'ex_conjoint';

  const handleSubmit = () => {
    if (!canSubmit) return;
    const rel = RELATIONS.find(r => r.value === relationType)!;
    const src = rel.swap ? (targetId as number) : (sourceId as number);
    const tgt = rel.swap ? (sourceId as number) : (targetId as number);
    onSave({
      membre_source: src,
      membre_cible: tgt,
      type_relation: rel.dbType,
      date_debut: isConjugal && dateDebut ? dateDebut : undefined,
      date_fin: isDivorce && dateFin ? dateFin : undefined,
    });
  };

  const selectedRelation = RELATIONS.find(r => r.value === relationType);
  const relationLabel = selectedRelation?.label;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Typography variant="h6" fontWeight={700} component="span">
          Créer une relation
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          {/* Visual preview */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              p: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <MiniAvatar membre={source} />
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5} noWrap maxWidth={80}>
                {source ? source.prenom : '—'}
              </Typography>
            </Box>

            <Box sx={{ flex: 1, textAlign: 'center', px: 0.5 }}>
              <Typography
                variant="caption"
                color={relationLabel ? 'primary.main' : 'text.disabled'}
                fontWeight={relationLabel ? 600 : 400}
                sx={{ fontSize: '0.72rem' }}
              >
                {relationLabel || '···'}
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <MiniAvatar membre={target} />
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5} noWrap maxWidth={80}>
                {target ? target.prenom : '—'}
              </Typography>
            </Box>
          </Box>

          <FormControl fullWidth size="small">
            <InputLabel>Personne A</InputLabel>
            <Select
              value={sourceId}
              onChange={e => setSourceId(e.target.value as number)}
              label="Personne A"
            >
              {membresTries.map(m => (
                <MenuItem key={m.id} value={m.id}>
                  {m.nom} {m.prenom}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Type de relation</InputLabel>
            <Select
              value={relationType}
              onChange={e => setRelationType(e.target.value)}
              label="Type de relation"
            >
              {RELATIONS.map(r => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Personne B</InputLabel>
            <Select
              value={targetId}
              onChange={e => setTargetId(e.target.value as number)}
              label="Personne B"
            >
              {membresTries
                .filter(m => m.id !== sourceId)
                .map(m => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.nom} {m.prenom}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {isConjugal && (
            <Stack direction="row" spacing={1.5}>
              <TextField
                label="Date de mariage"
                type="date"
                value={dateDebut}
                onChange={e => setDateDebut(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              {isDivorce && (
                <TextField
                  label="Date de divorce"
                  type="date"
                  value={dateFin}
                  onChange={e => setDateFin(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" size="small">
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="small"
          disabled={!canSubmit}
          disableElevation
        >
          Créer la relation
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RelationFormDialog;
